import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Divider, Radio } from '@mantine/core';
import AppStack from './AppStack';
import ResponsiveModal from './ResponsiveModal';
import AppText from './AppText';
import CustomSelect from './CustomSelect';
import { singularPluralFormat } from '../../helpers/format';

const DuplicateResourcesModal = ({
  isLoading,
  isOpen,
  onSubmit,
  onClose,
  title,
  resourceName,
  resourceOptions,
  itemType,
  itemTypePlural,
  selectedItems,
  canMakePrivate,
  children
}) => {
  const [formState, setFormState] = useState({
    resourceId: '',
    copyAll: false,
    isPrivate: false,
    showConfirm: false
  });
  const selectedResource = resourceOptions?.find(
    (r) => r.value === formState.resourceId
  );

  useEffect(() => {
    if (isOpen) {
      setFormState({
        resourceId: '',
        copyAll: false,
        isPrivate: false,
        showConfirm: false
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showConfirm
          ? {
              onCancel: () => {
                setFormState({ ...formState, showConfirm: false });
              },
              onSubmit: () => onSubmit(formState),
              isLoading,
              submitTitle: 'Yes, copy resources',
              cancelTitle: 'Back'
            }
          : {
              onCancel: onClose,
              submitTitle: 'Continue',
              onSubmit: () => setFormState({ ...formState, showConfirm: true }),
              isSubmitDisabled: !formState.resourceId
            }
      }
      onClose={onClose}
      opened={isOpen}
      size={600}
      title={title}
    >
      {formState.showConfirm ? (
        <AppStack style={{ gap: 12 }}>
          <AppText style={{ fontSize: 14 }}>
            Are you sure you want to copy{' '}
            <span style={{ fontWeight: 500 }}>
              {formState.copyAll
                ? `all ${itemTypePlural}`
                : singularPluralFormat(
                    selectedItems?.length,
                    itemType,
                    itemTypePlural
                  )}
            </span>{' '}
            to{' '}
            <span style={{ fontWeight: 500 }}>{selectedResource?.label}</span>
          </AppText>

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            This action cannot be undone.
          </AppText>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 12 }}>
          <Radio.Group
            description={`Select the ${itemTypePlural} to copy`}
            label={`Copy ${itemTypePlural}`}
            onChange={(v) => {
              setFormState({
                ...formState,
                copyAll: v === 'all'
              });
            }}
            value={formState.copyAll ? 'all' : 'selected'}
          >
            <AppStack style={{ gap: 10, marginTop: 10 }}>
              <Radio label={`All ${itemTypePlural}`} value="all" />
              <Radio
                disabled={!selectedItems || selectedItems.length === 0}
                label={`Selected ${singularPluralFormat(
                  selectedItems?.length ?? 0,
                  itemType,
                  itemTypePlural
                )}`}
                value="selected"
              />
            </AppStack>
          </Radio.Group>

          <Divider />

          <AppStack style={{ gap: 16 }}>
            <AppStack style={{ gap: 12 }}>
              <CustomSelect
                data={resourceOptions || []}
                disabled={isLoading}
                label={resourceName}
                onChange={(v) => setFormState({ ...formState, resourceId: v })}
                placeholder={`Select ${resourceName?.toLowerCase()}`}
                value={formState.resourceId}
              />

              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                Please choose a {resourceName?.toLowerCase()} to copy the
                resources to.
              </AppText>
            </AppStack>

            {canMakePrivate && (
              <Checkbox
                checked={formState.isPrivate}
                description="When selected, this product will be hidden from the public catalog but can still be added to any store"
                label="Visible to SportsHeadz admins only"
                onChange={() =>
                  setFormState({
                    ...formState,
                    isPrivate: !formState.isPrivate
                  })
                }
              />
            )}

            {children}
          </AppStack>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

DuplicateResourcesModal.propTypes = {
  canMakePrivate: PropTypes.bool,
  children: PropTypes.any,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  itemType: PropTypes.string,
  itemTypePlural: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  resourceName: PropTypes.string,
  resourceOptions: PropTypes.array,
  selectedItems: PropTypes.array,
  title: PropTypes.string
};

export default DuplicateResourcesModal;
